import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Autocomplete, CircularProgress, TextField, Tooltip, FormHelperText, FormControl} from "@mui/material";
import { Controller } from "react-hook-form";
import HelpIcon from "@mui/icons-material/Help";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ReadOnlyInput from "./ReadOnlyInput";
import InputWrapper from "./InputWrapper";
import { DEFAULT_SIZE } from "../../../utils/constant";

const sameId = (a, b) => String(a) === String(b);

const isEmpty = (val) => val === "" || val === null || typeof val === "undefined";

const findOptionByValue = (options, value) => options.find((opt) => sameId(opt.value, value));

const InfiniteScrollSelectField = ({
  onChange,
  value,
  options = [],
  label,
  fieldProps,
  onBlur,
  placeholder,
  loading = false,
  hasMore = false,
  loadMore,
  onSearch,
  currentPage = 1,
  allowManualEntry = false,
  withInfoIcon = false,
  infoMessage = "",
  error,
  rules,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState("");
  const [searchText, setSearchText] = useState("");
  const observerRef = useRef(null);

  useEffect(() => {
    if (isEmpty(value)) {
      setInputValue("");
      return;
    }

    const matched = findOptionByValue(options, value);
    if (matched) {
      setInputValue(matched.label);
    }
  }, [value, options]);

  const lastOptionRef = useCallback(
    (node) => {
      if (loading || !hasMore) return;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          loadMore?.(currentPage + 1, searchText || "");
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore, loadMore, currentPage, searchText]
  );

  const selectedOption = useMemo(() => {
    if (isEmpty(value)) return null;
    const matched = findOptionByValue(options, value);
    return matched || null;
  }, [options, value]);

  const handleChange = useCallback(
    (_, newValue) => {
      if (!onChange) return;
      if (newValue && typeof newValue === "object") {
        onChange(newValue.value);
        setInputValue(newValue.label);
        return;
      }

      if (typeof newValue === "string") {
        onChange(newValue);
        setInputValue(newValue);
        return;
      }
      onChange("");
      setInputValue("");
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (_, newInputValue, reason) => {
      const nextValue = newInputValue ?? "";
      setInputValue(nextValue);

      if (reason === "input" || reason === "clear") {
        const term = reason === "clear" ? "" : nextValue;
        setSearchText(term);
        onSearch?.(1, term || "");
      }

      if (allowManualEntry && (reason === "input" || reason === "clear")) {
        onChange?.(nextValue);
      }
    },
    [allowManualEntry, onChange, onSearch]
  );

  const hasError = !!error && (typeof error === "string" || !!error?.message);
  const helperText = typeof error === "string" ? error : error?.message || "";

  return (
    <FormControl className="proInputWithMessage" fullWidth error={hasError}>
      <div className="proInputWithMessage-content">
        <div className={withInfoIcon ? "proInputWithMessage-input" : "proInputWithMessage-input-full"}>
          <Autocomplete
            fullWidth
            disablePortal
            blurOnSelect
            freeSolo={allowManualEntry}
            options={options}
            loading={loading}
            value={selectedOption}
            inputValue={inputValue}
            getOptionLabel={(opt) => opt ? (typeof opt === "string" ? opt : opt.label ?? "") : ""}
            isOptionEqualToValue={(opt, val) =>
              Boolean(
                opt &&
                  val &&
                  sameId(
                    opt.value,
                    typeof val === "object" ? val.value : val
                  )
              )
            }
            onChange={handleChange}
            onInputChange={handleInputChange}
            filterOptions={(x) => x}
            ListboxProps={{ style: { maxHeight: 300, overflow: "auto" } }}
            popupIcon={<ArrowDropDownIcon />}
            forcePopupIcon
            renderOption={(props, option) => {
              const isLast = options.indexOf(option) === options.length - 1;
              return (
                <li {...props} ref={isLast ? lastOptionRef : null}>
                  {option.label}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={<span>{label}</span>}
                InputLabelProps={{ shrink: true }}
                placeholder={placeholder || label}
                variant="outlined"
                fullWidth
                onBlur={(e) => {
                  const typedValue = e?.target?.value?.trim() ?? "";
                  onBlur?.(e);

                  if (!typedValue) {
                    if (!isEmpty(value)) {
                      onChange?.("");
                    }
                    setInputValue("");
                    setSearchText("");
                    onSearch?.(1, "");
                    return;
                  }

                  const normalized = typedValue.toLowerCase();

                  const matchedOption = options.find(
                    (opt) => opt?.label?.toLowerCase().trim() === normalized || String(opt?.value) ?.toLowerCase().trim() === normalized
                  );

                  if (matchedOption) {
                    if (!sameId(matchedOption.value, value)) {
                      onChange?.(matchedOption.value);
                    }
                    setInputValue(matchedOption.label);
                    return;
                  }

                  onChange?.("");
                  setInputValue("");
                  setSearchText("");
                  onSearch?.(1, "");
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            {...fieldProps}
            {...rest}
          />
        </div>

        {withInfoIcon && (
          <div className="proInputWithMessage-info">
            <Tooltip title={infoMessage} placement="top">
              <HelpIcon color="primary" />
            </Tooltip>
          </div>
        )}
      </div>

      <FormHelperText error={hasError}>{helperText}</FormHelperText>
    </FormControl>
  );
};

InfiniteScrollSelectField.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.any,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
    })
  ).isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  fieldProps: PropTypes.object,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
  hasMore: PropTypes.bool,
  loadMore: PropTypes.func,
  onSearch: PropTypes.func,
  allowManualEntry: PropTypes.bool,
  withInfoIcon: PropTypes.bool,
  infoMessage: PropTypes.string,
  error: PropTypes.oneOfType([
    PropTypes.shape({ message: PropTypes.string }),
    PropTypes.string,
  ]),
  rules: PropTypes.object,
  currentPage: PropTypes.number,
};

const InfiniteScrollSelect = ({
  name,
  mode,
  label,
  rules,
  control,
  infoMessage,
  isAddMode,
  options = [],
  fieldProps = {},
  size = DEFAULT_SIZE,
  withInfoIcon = false,
  permission = {},
  placeholder,
  currentPage = 1,
  ...rest
}) => {
  const { canView = false, canAdd = false, canEdit = false } =
    permission || {};

  if (!canView || (isAddMode && !canAdd)) return null;

  const editModeAllowed = isAddMode ? canAdd : canEdit;

  if (mode === "read" || (mode === "edit" && !editModeAllowed)) {
    return (
      <ReadOnlyInput
        size={size}
        type="select"
        label={label}
        name={name}
        options={options || []}
        rules={rules}
        {...rest}
      />
    );
  }

  return (
    <InputWrapper size={size}>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({
          field: { onChange, onBlur, value },
          fieldState: { error },
        }) => (
          <InfiniteScrollSelectField
            onChange={onChange}
            value={value}
            placeholder={label || placeholder}
            options={options}
            error={error}
            label={
              <span>
                {label}
                {rules?.required && (
                  <span style={{ color: "red" }}> *</span>
                )}
              </span>
            }
            fieldProps={fieldProps}
            withInfoIcon={withInfoIcon}
            infoMessage={infoMessage}
            loading={rest.loading}
            allowManualEntry={rest.allowManualEntry}
            onBlur={onBlur}
            rules={rules}
            currentPage={currentPage}
            {...rest}
          />
        )}
      />
    </InputWrapper>
  );
};

InfiniteScrollSelect.propTypes = {
  name: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["read", "edit"]).isRequired,
  label: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  rules: PropTypes.object,
  infoMessage: PropTypes.string,
  isAddMode: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
    })
  ),
  fieldProps: PropTypes.object,
  size: PropTypes.string,
  withInfoIcon: PropTypes.bool,
  permission: PropTypes.shape({
    canView: PropTypes.bool,
    canAdd: PropTypes.bool,
    canEdit: PropTypes.bool,
  }),
  placeholder: PropTypes.string,
  currentPage: PropTypes.number,
};

export default InfiniteScrollSelect;